import { isDobValid, subtractYrsFromDate } from "@tm/insurance/utils";
import type { FieldConfig, FormConfig } from "../../types";

// ── Date constants ──────────────────────────────────────────────────────

export const minDate = subtractYrsFromDate(new Date(), 70)
  .toISOString()
  .split("T")[0];
export const maxDate = new Date().toISOString().split("T")[0];

// ── Date conversions ────────────────────────────────────────────────────

export const convertDDMMYYYYToYYYYMMDD = (dateStr: string): string => {
  if (!dateStr) return "";
  const parts = dateStr.split("/");
  if (parts.length === 3) {
    const [day, month, year] = parts;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }
  return dateStr;
};

export const convertYYYYMMDDToDDMMYYYY = (dateStr: string): string => {
  if (!dateStr) return "";
  if (dateStr.includes("/")) return dateStr;
  const parts = dateStr.split("-");
  if (parts.length === 3) {
    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
  }
  return dateStr;
};

// ── Field validation ────────────────────────────────────────────────────

export const validateField = (value: string, field: FieldConfig): string => {
  if (!field.validation) return "";

  if (field.isRequired && (!value || value.trim() === "")) {
    return field.validation.errorMessage || "This field is required";
  }

  if (value && field.type === "DATE" && field.validation.pattern) {
    let dateValue: string | Date = value;

    if (value.includes("-") && value.length === 10) {
      const dateObj = new Date(value);
      if (isNaN(dateObj.getTime())) {
        return field.validation.errorMessage || "Invalid date";
      }
      dateValue = dateObj;
    } else if (value.includes("/")) {
      const parts = value.split("/");
      if (parts.length === 3) {
        const [day, month, year] = parts;
        const dateObj = new Date(
          `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`
        );
        if (isNaN(dateObj.getTime())) {
          return field.validation.errorMessage || "Invalid date";
        }
        dateValue = dateObj;
      }
    }

    const pattern = field.validation.pattern.replace(/\\\\/g, "\\");
    const isValid = isDobValid(dateValue, pattern);
    if (!isValid) {
      return field.validation.errorMessage || "Invalid date";
    }
  } else if (value && field.validation.pattern) {
    const pattern = field.validation.pattern.replace(/\\\\/g, "\\");
    const regex = new RegExp(pattern);
    if (!regex.test(value)) {
      return field.validation.errorMessage || "Invalid value";
    }
  }

  return "";
};

// ── Normalization (API sends fields as array OR object map) ─────────────

export const normalizeFields = (
  fields: FieldConfig[] | Record<string, any> | undefined
): FieldConfig[] => {
  if (!fields) return [];
  if (Array.isArray(fields)) return fields;
  return Object.entries(fields).map(([key, value]) => ({
    ...value,
    name: value.name ?? key,
  }));
};

export const normalizeFormConfig = (raw: FormConfig): FormConfig => ({
  ...raw,
  fields: normalizeFields(raw.fields),
  dynamicFields: raw.dynamicFields?.map((df) => ({
    ...df,
    fields: normalizeFields(df.fields),
  })),
});
